import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { initLocale, isRTL, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { SessionProvider } from '@/core/session';
import { ThemeProvider } from '@/ui/theme';
import { NewSignalGuard } from '@/features/signals/NewSignalGuard';
import { Shell } from './Shell';
import { AppRoutes } from './routes';
import { SignupGateProvider } from '@/features/signup/SignupGate';

// همون تنظیماتی که اپِ موبایل داره؛ داده‌ی این اپ سریع کهنه می‌شه، پس
// refetch روی فوکوس روشنه ولی داده تا یک دقیقه تازه حساب می‌شه.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 1 } },
});

/**
 * هر کوئری‌ای که شکست بخوره — حتی اون‌هایی که خودشون خطا رو نشون نمی‌دن —
 * ثبت می‌شه. قرینه‌ی همین چند خط در apps/mobile/app/_layout.tsx.
 *
 * تا امروز فقط اپ این رو داشت، و نتیجه‌ش این بود که یه کوئریِ شکسته روی
 * سایت هیچ‌جا دیده نمی‌شد جز یه فهرستِ خالی. `reportError` هم `client_errors`
 * رو پر می‌کنه هم سطرِ خودش رو توی لاگِ فعالیت می‌ذاره.
 */
queryClient.getQueryCache().subscribe((ev) => {
  if (ev.type === 'updated' && ev.query.state.status === 'error') {
    reportError(`query.${String(ev.query.queryKey[0])}`, ev.query.state.error);
  }
});

/** جهتِ صفحه با زبان عوض می‌شه — فارسی/عربی/اردو راست‌به‌چپ‌اند. */
function Direction({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRTL() ? 'rtl' : 'ltr';
  }, [locale]);
  return <>{children}</>;
}

export function App() {
  // زبان قبل از اولین رندر خونده می‌شه، وگرنه سایت انگلیسی بالا میاد و بعد
  // می‌پره روی زبانِ درست.
  const [localeReady, setLocaleReady] = useState(false);
  useEffect(() => { initLocale().then(() => setLocaleReady(true)); }, []);
  if (!localeReady) return null;

  return (
    <ThemeProvider>
      <Direction>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <BrowserRouter>
              {/* پنجره‌ی «هنوز دوستی نداری» یه بار این‌جا ساخته می‌شه و هر
                  دکمه‌ی «سیگنالِ جدید» از پشتِ کانتکستش رد می‌شه. داخلِ
                  روتره چون خودش هم می‌تونه به صفحه‌ی دوستان ببره. */}
              <NewSignalGuard>
                {/* برگه‌ی ثبت‌نامِ مهمان، یکی برای کلِ سایت (WHOSUP-308). */}
                <SignupGateProvider>
                  <Shell><AppRoutes /></Shell>
                </SignupGateProvider>
              </NewSignalGuard>
            </BrowserRouter>
          </SessionProvider>
        </QueryClientProvider>
      </Direction>
    </ThemeProvider>
  );
}
